"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { LivePreferences } from "@/app/api/users/me/preferences/route";

const DEFAULTS: LivePreferences = { fontSize: "base", fontFamily: "serif", chordDisplay: "chord", noteLanguage: "en" };
const SAVE_DEBOUNCE_MS = 800;

type DisplayPreferencesContextValue = {
  prefs: LivePreferences;
  loaded: boolean;
  setFontSize: (v: LivePreferences["fontSize"]) => void;
  setFontFamily: (v: LivePreferences["fontFamily"]) => void;
  setChordDisplay: (v: LivePreferences["chordDisplay"]) => void;
  setNoteLanguage: (v: LivePreferences["noteLanguage"]) => void;
};

const DisplayPreferencesContext = createContext<DisplayPreferencesContextValue | null>(null);

// Un seul fetch/état par onglet : sans ce Provider, chaque composant qui
// appelle useDisplayPreferences() aurait son propre état local, et changer
// une préférence dans l'un ne mettrait pas à jour l'affichage des autres
// tant que la page n'est pas rechargée.
export function DisplayPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<LivePreferences>(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef<LivePreferences>(DEFAULTS);

  useEffect(() => {
    fetch("/api/users/me/preferences")
      .then((r) => r.json())
      .then(({ data }) => {
        if (data) {
          setPrefs(data);
          latestRef.current = data;
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  function save(next: LivePreferences) {
    latestRef.current = next;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetch("/api/users/me/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(latestRef.current),
      }).catch(() => {});
    }, SAVE_DEBOUNCE_MS);
  }

  function setFontSize(fontSize: LivePreferences["fontSize"]) {
    const next = { ...latestRef.current, fontSize };
    setPrefs(next);
    save(next);
  }

  function setFontFamily(fontFamily: LivePreferences["fontFamily"]) {
    const next = { ...latestRef.current, fontFamily };
    setPrefs(next);
    save(next);
  }

  function setChordDisplay(chordDisplay: LivePreferences["chordDisplay"]) {
    const next = { ...latestRef.current, chordDisplay };
    setPrefs(next);
    save(next);
  }

  function setNoteLanguage(noteLanguage: LivePreferences["noteLanguage"]) {
    const next = { ...latestRef.current, noteLanguage };
    setPrefs(next);
    save(next);
  }

  return (
    <DisplayPreferencesContext.Provider
      value={{ prefs, loaded, setFontSize, setFontFamily, setChordDisplay, setNoteLanguage }}
    >
      {children}
    </DisplayPreferencesContext.Provider>
  );
}

const FALLBACK: DisplayPreferencesContextValue = {
  prefs: DEFAULTS,
  loaded: false,
  setFontSize: () => {},
  setFontFamily: () => {},
  setChordDisplay: () => {},
  setNoteLanguage: () => {},
};

export function useDisplayPreferences(): DisplayPreferencesContextValue {
  return useContext(DisplayPreferencesContext) ?? FALLBACK;
}
